import Promise from 'bluebird'
import CreateReducer from '@nocode-works/template/store/utils/createReducer'
import CreateActions from '@nocode-works/template/store/utils/createActions'
import networkWrapper from '@nocode-works/template/store/utils/networkWrapper'
import {
  handlers,
} from '@nocode-works/template/store/utils/api'

import snackbarActions from '@nocode-works/template/store/modules/snackbar'
import websiteActions from '@nocode-works/template/store/modules/website'
import stripeSelectors from '../selectors/stripe'

import {
  STRIPE_PUBLIC_KEY
} from 'settings'

const prefix = 'stripe'
const wrapper = networkWrapper.factory(prefix)

const initialState = {
  products: [],
  card: null,
  invoices: [],
  cancelledSubscriptionInvoices: [],
  cancelledSubscriptions: [],
  customer: null,
  cancelSubscriptionWindowOpen: false,
  reactivateSubscriptionWindowOpen: false,
  viewCancelledSubscription: null,
  changePlanWebsite: null,
  // this is the proation info from stripe
  // that we use to display the change in billing
  // for the rest of the month for the upgrade
  changePlanInfo: null,
}

const reducers = {
  setProducts: (state, action) => {
    state.products = action.payload
  },
  setCustomer: (state, action) => {
    state.customer = action.payload
  },
  setCard: (state, action) => {
    state.card = action.payload
  },
  setInvoices: (state, action) => {
    state.invoices = action.payload
  },
  setCancelledSubscriptions: (state, action) => {
    state.cancelledSubscriptions = action.payload
  },
  setCancelSubscriptionWindowOpen: (state, action) => {
    state.cancelSubscriptionWindowOpen = action.payload
  },
  setReactivateSubscriptionWindowOpen: (state, action) => {
    state.reactivateSubscriptionWindowOpen = action.payload
  },
  setChangePlanWebsite: (state, action) => {
    state.changePlanWebsite = action.payload
  },
  setChangePlanInfo: (state, action) => {
    state.changePlanInfo = action.payload
  },
  setViewCancelledSubscription: (state, action) => {
    state.viewCancelledSubscription = action.payload
  },
  setCancelledSubscriptionInvoices: (state, action) => {
    state.cancelledSubscriptionInvoices = action.payload
  },
}

const sideEffects = {

  loadAll: () => async (dispatch, getState) => {
    dispatch(actions.loadProducts())
    dispatch(actions.loadCustomer())
    dispatch(actions.loadCard())
    dispatch(actions.loadInvoices())
    dispatch(actions.loadCancelledSubscriptions())
    dispatch(websiteActions.list())
  },

  loadAllBlocking: () => async (dispatch, getState) => {
    await Promise.all([
      dispatch(actions.loadProducts()),
      dispatch(actions.loadCustomer()),
      dispatch(actions.loadCard()),
      dispatch(actions.loadInvoices()),
      dispatch(actions.loadCancelledSubscriptions()),
      dispatch(websiteActions.list()),
    ])
  },

  loadProducts: () => wrapper('loadProducts', async (dispatch, getState) => {
    const data = await handlers.get(`/stripe/products`)
    dispatch(actions.setProducts(data))
  }),

  loadCustomer: () => wrapper('loadCustomer', async (dispatch, getState) => {
    const data = await handlers.get(`/stripe/customer`)
    dispatch(actions.setCustomer(data))
  }),

  loadCard: () => wrapper('loadCard', async (dispatch, getState) => {
    const data = await handlers.get(`/stripe/card`)
    dispatch(actions.setCard(data))
  }),

  loadInvoices: () => wrapper('loadInvoices', async (dispatch, getState) => {
    const data = await handlers.get(`/stripe/invoices`)
    dispatch(actions.setInvoices(data))
  }),

  loadCancelledSubscriptions: () => wrapper('loadCancelledSubscriptions', async (dispatch, getState) => {
    const data = await handlers.get(`/stripe/subscriptions/cancelled`)
    dispatch(actions.setCancelledSubscriptions(data))
  }),

  loadCancelledSubscriptionInvoices: (id) => wrapper('loadCancelledSubscriptionInvoices', async (dispatch, getState) => {
    const data = await handlers.get(`/stripe/subscription/${id}/invoices`)
    dispatch(actions.setCancelledSubscriptionInvoices(data))
  }),

  changeWebsitePlan: ({
    website,
    product,
    price,
    prorationDate,
  }) => wrapper('changeWebsitePlan', async (dispatch, getState) => {
    const pageUrl = document.location.href
    const result = await handlers.post(`/stripe/website/${website}/subscription`, {
      product,
      price,
      pageUrl,
      prorationDate,
    })
    if(result.action == 'stripe_session') {
      if(!result.session) throw new Error(`no session found`)
      const stripe = Stripe(STRIPE_PUBLIC_KEY)
      const {error} = await stripe.redirectToCheckout({
        sessionId: result.session.id,
      })
      if(error) throw new Error(error)
    }
    else if(result.action == 'currency_error') {
      dispatch(snackbarActions.setError(`You must use the ${result.currency} currency or cancel this subscription`))
    }
    else if(result.action == 'update_confirmed') {
      await dispatch(actions.loadAllBlocking())
      dispatch(actions.setChangePlanWebsite(null))
      dispatch(actions.setChangePlanInfo(null))
      dispatch(snackbarActions.setSuccess(`Your subscription has been updated`))
    }
    else if(result.action == 'change_plan_confirmation') {
      dispatch(actions.setChangePlanInfo({
        website,
        product,
        price,
        result,
      }))
    }
    else {
      throw new Error(`unknown changeWebsitePlan action: ${result.action}`)
    }
  }, {
    autoLoading: true,
  }),

  changeCard: ({

  } = {}) => wrapper('changeCard', async (dispatch, getState) => {
    const pageUrl = document.location.href
    const result = await handlers.post(`/stripe/subscription/card`, {pageUrl})
    if(result.action == 'stripe_session') {
      if(!result.session) throw new Error(`no session found`)
      const stripe = Stripe(STRIPE_PUBLIC_KEY)
      const {error} = await stripe.redirectToCheckout({
        sessionId: result.session.id,
      })
      if(error) throw new Error(error)
    }
    else {
      throw new Error(`unknown changeCard action: ${result.action}`)
    }
  }),

  cancelSubscription: ({

  } = {}) => wrapper('cancelSubscription', async (dispatch, getState) => {
    await handlers.post(`/stripe/subscription/cancel`)
    await dispatch(actions.loadAllBlocking())
    dispatch(snackbarActions.setSuccess(`Your subscription has been cancelled`))
    dispatch(actions.setCancelSubscriptionWindowOpen(false))
  }),

  reactivateSubscription: ({

  } = {}) => wrapper('reactivateSubscription', async (dispatch, getState) => {
    await handlers.post(`/stripe/subscription/reactivate`)
    await dispatch(actions.loadAllBlocking())
    dispatch(snackbarActions.setSuccess(`Your subscription has been reactivated`))
    dispatch(actions.setReactivateSubscriptionWindowOpen(false))
  }),

  confirmSubscriptionChange: ({

  } = {}) => async (dispatch, getState) => {
    const changePlanInfo = stripeSelectors.changePlanInfo(getState())
    const {
      website,
      product,
      price,
      result,
    } = changePlanInfo
    const prorationDate = result.invoice.subscription_proration_date
    dispatch(actions.changeWebsitePlan({
      website,
      product,
      price,
      prorationDate,
    }))
  }
}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export { actions, reducer }
export default actions